import { alpha, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Theme, Typography } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { Business_Profile_Enum } from '@kheops/graphql';
import { useTranslation } from 'react-i18next';
import WishlistActiveReminderHeaderImage from '../../assets/images/wishlist-active-reminder-header.png';
import FileUtils from '../../common/utils/file.utils';
import useWishlistActiveReminder from './useWishlistActiveReminder';

const innerShadowSx = (theme: Theme): SystemStyleObject<Theme> => ({
  position: 'relative',
  display: 'flex',
  maxWidth: 360,
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    zIndex: 10,
    boxShadow: `inset -32px 0 36px 0 ${alpha(theme.palette.surfaceContainerLowest.main, 0.95)}`,
  },
});

export default function WishlistActiveReminderDialog(): React.JSX.Element {
  const { t } = useTranslation(['wishlist', 'common']);
  const {
    open,
    onClose,
    onCtaClick,
    previewProducts,
    previewSuppliers,
  } = useWishlistActiveReminder();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      sx={{
        [`& .MuiPaper-root.MuiDialog-paper`]: {
          maxWidth: 580,
        },
      }}
    >
      <DialogTitle variant="image">
        <Box
          component="img"
          src={WishlistActiveReminderHeaderImage}
          sx={{
            aspectRatio: '3 / 1',
            objectFit: 'cover',
            width: '100%',
          }}
        />
      </DialogTitle>
      <DialogContent sx={{ py: 1.5, overflowX: 'hidden' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, textAlign: 'center', px: 3, pt: 1.5 }}>
            <Typography variant="headlineMedium">
              {t('wishlist:active_reminder_title')}
            </Typography>
            <Typography variant="bodyMedium">
              {t('wishlist:active_reminder_description')}
            </Typography>
          </Box>

          {previewProducts.length > 0 && (
            <Box sx={innerShadowSx}>
              {previewProducts.map((product, index) => (
                <Box
                  key={product.id}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    mr: index < previewProducts.length - 1 ? -0.5 : 0,
                    border: '1px solid',
                    borderColor: 'outlineVariant.main',
                    borderRadius: 3,
                    bgcolor: 'background.paper',
                    zIndex: previewProducts.length - index,
                    flexShrink: 0,
                  }}
                >
                  <Box
                    component="img"
                    src={FileUtils.BuildPackagingImageUrl({
                      photo: product.photo,
                      type: product.family,
                      size: { height: 56, fit: 'contain' },
                    })}
                    sx={{ width: 56, height: 56, objectFit: 'contain' }}
                  />
                  <Typography variant="titleSmall">
                    {product.priceLabel}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {previewSuppliers.length > 0 && (
            <Box sx={[{ py: 0.5 }, innerShadowSx]}>
              {previewSuppliers.map((supplier, index) => (
                <Box
                  key={supplier.id}
                  component="img"
                  src={FileUtils.BuildCompanyImageUrl({
                    photo: supplier.photo,
                    type: Business_Profile_Enum.Supplier,
                    size: { width: 72, height: 72, fit: 'cover' },
                  })}
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: '100px',
                    border: '1px solid',
                    borderColor: 'outlineVariant.main',
                    objectFit: 'cover',
                    mr: index < previewSuppliers.length - 1 ? -4 : 0,
                    zIndex: previewSuppliers.length - index,
                    flexShrink: 0,
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          {t('common:close')}
        </Button>
        <Button variant="contained" onClick={onCtaClick}>
          {t('wishlist:active_reminder_cta')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
